import { Button } from "@heroui/button";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/modal";
import { Progress } from "@heroui/progress";
import { SkillType } from "~/types/SkillLevel";
import { XPChip } from "../XPChip";
import { Constants } from "~/lib/constants";
import { cn } from "@heroui/react";
import { getSkillStageByLevel } from "~/utils/skills";
import posthog from "posthog-js";
import { useContext } from "react";
import { AppContext } from "~/context/AppContext";
import { useNavigate } from "@remix-run/react";

export const SkillDescriptionModal = ({
  skill,
  isOpen,
  onOpenChange,
  isComingSoon = false,
  practiceSkill,
}: {
  skill: SkillType;
  isOpen: boolean;
  onOpenChange: () => void;
  isComingSoon: boolean;
  practiceSkill?: (skillName?: SkillType["name"]) => void | undefined;
}) => {
  const { gameData } = useContext(AppContext);
  const skillConstants = Constants.SKILLS[skill.name];
  const currentStage = getSkillStageByLevel(skill.level);
  const navigate = useNavigate();
  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} scrollBehavior="inside">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col">
              <div className="flex flex-row gap-4 font-bangers items-center">
                {skill.name}

                {isComingSoon ? (
                  <span className="text-xs rounded-full text-neutral-400 border-2 border-neutral-400 py-1 px-2">
                    Coming Soon
                  </span>
                ) : (
                  <XPChip
                    xp={skill.currentXP}
                    totalXP={skill.nextLevelRequiredXP}
                    className="font-sans text-sm"
                  />
                )}
              </div>
              <div className="flex flex-row w-full items-center mt-2 font-chewy gap-2">
                <p className="">{skill.level}</p>

                <Progress
                  aria-label="progress bar"
                  color="warning"
                  value={skill.currentXP}
                  minValue={skill.currentLevelRequiredXP}
                  maxValue={skill.nextLevelRequiredXP}
                />
                {skill.level < 10 && (
                  <p className="">{Math.max(skill.level + 1)}</p>
                )}
              </div>
            </ModalHeader>
            <ModalBody className="">
              <p className="text-sm leading-tight opacity-70">
                {skill.description}
              </p>
              <div className="pb-5">
                {/* for each icon  */}
                {Object.entries(skillConstants.stages).map(
                  ([stage, data], index) => {
                    return (
                      <div
                        key={stage}
                        className="flex flex-row gap-4 mt-8 items-center"
                      >
                        <div className="flex flex-col items-center h-full justify-center">
                          {data.icon(56)}
                        </div>
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-row items-center gap-2">
                            <h1
                              className={cn(
                                "font-bangers text-xl leading-none"
                              )}
                              style={{
                                color:
                                  index + 1 === currentStage
                                    ? skillConstants.color
                                    : "",
                              }}
                            >
                              Stage {index + 1}
                            </h1>
                            {index + 1 === currentStage ? (
                              <span className="relative flex size-3">
                                <span
                                  className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"
                                  style={{
                                    backgroundColor: skillConstants.color,
                                  }}
                                ></span>
                                <span
                                  className="relative inline-flex size-3 rounded-full"
                                  style={{
                                    backgroundColor: skillConstants.color,
                                  }}
                                ></span>
                              </span>
                            ) : (
                              <p className="text-xs opacity-70">
                                Unlock at level {(index + 1) * 2 - 1}
                              </p>
                            )}
                          </div>
                          <p className="text-sm opacity-70">
                            {data.description}
                          </p>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </ModalBody>
            {practiceSkill && (
              <ModalFooter>
                <Button
                  onPress={async () => {
                    if (!gameData) {
                      navigate("/sign-up");
                      return;
                    }
                    posthog.capture(`practice_skill_${skill.name}`);
                    onClose();
                    setTimeout(async () => {
                      await practiceSkill(skill.name);
                    }, 500);
                  }}
                  color="warning"
                  isDisabled={isComingSoon}
                  className="font-bangers text-lg tracking-wide"
                >
                  {!gameData
                    ? "Create Account"
                    : isComingSoon
                    ? "Coming Soon"
                    : "Practice"}
                </Button>
              </ModalFooter>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
